import { useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const staticLinks = [
  { label: '首页', to: '/' },
  { label: '常见问题', to: '/faq' },
  { label: '关于我们', to: '/about' },
  { label: '联系我们', to: '/contact' },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  const today = new Date().toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMenuOpen(false);
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap font-label text-sm transition-colors ${
      isActive ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-300/70 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 md:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-lg' : 'ri-menu-line text-lg'} />
          </button>
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 font-heading text-lg font-bold text-background-50">
              瓜
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
                美日吃瓜
              </span>
              <span className="mt-0.5 hidden font-label text-[10px] uppercase tracking-[0.24em] text-foreground-500 sm:block">
                政经暗面观察站
              </span>
            </span>
          </Link>
        </div>

        <form onSubmit={handleSearch} className="hidden flex-1 max-w-xs lg:block">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索爆料、机构或关键词"
              className="w-full rounded-full border border-background-300 bg-background-100 py-2 pl-9 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
        </form>

        <div className="hidden items-center gap-5 md:flex">
          <span className="hidden font-label text-xs text-foreground-500 xl:block">{today}</span>
          <NavLink to="/about" className={navClass}>
            关于我们
          </NavLink>
          <Link
            to="/contact"
            className="inline-flex items-center rounded-full bg-primary-600 px-4 py-2 font-label text-sm font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-primary-700"
          >
            投递线索
          </Link>
        </div>
      </div>

      <nav aria-label="栏目导航" className="hidden border-t border-background-300/70 md:block">
        <div className="mx-auto flex max-w-6xl items-center gap-7 px-4 md:px-6">
          <div className="flex h-12 items-center gap-7 overflow-x-auto">
            {staticLinks.slice(0, 1).map((link) => (
              <NavLink key={link.to} to={link.to} className={navClass} end>
                {link.label}
              </NavLink>
            ))}
            {categories.map((category) => (
              <NavLink key={category.slug} to={`/category/${category.slug}`} className={navClass}>
                {category.name}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {menuOpen ? (
        <div className="border-t border-background-300/70 bg-background-50 md:hidden">
          <div className="mx-auto max-w-6xl px-4 py-4">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索爆料、机构或关键词"
                  className="w-full rounded-full border border-background-300 bg-background-100 py-2.5 pl-9 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category) => (
                <NavLink
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                    <i className={category.icon} />
                  </span>
                  {category.name}
                </NavLink>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 border-t border-background-300/70 pt-3">
              {staticLinks.slice(1).map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-full bg-secondary-100 px-3.5 py-1.5 font-label text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}